// ============================================================================
// KLINISOL CRO - SOP Upload Page (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// Super Admin Only
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SOPUploadForm from '../components/SOPUploadForm';
import { 
    ArrowLeft, 
    PowerOff, 
    Upload, 
    Shield, 
    CheckCircle,
    AlertCircle 
} from 'lucide-react';

const SOPUpload = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleUploadSuccess = (sopData) => {
        console.log('SOP uploaded successfully:', sopData);
        // Optionally redirect to SOP library
        // navigate('/sop-library');
    };

    // Check if user is Super Admin
    if (user?.id !== 1) {
        return (
            <div className="min-h-screen bg-[#f8fafd] flex items-center justify-center p-6">
                <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-600 mb-6">Only Super Admins can upload SOPs.</p>
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#E91E63] text-white font-semibold rounded-lg hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/sop-library')}
                                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg transition-all"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                <span className="font-medium">Back</span>
                            </button>
                            <div>
                                <h1 className="text-2xl md:text-3xl font-bold">
                                    KLIN<span className="text-[#E91E63]">i</span>SOL SOP Upload
                                </h1>
                                <p className="text-sm opacity-90">Standard Operating Procedure Management</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right mr-3">
                                <p className="font-semibold text-sm">{user?.firstName} {user?.lastName}</p>
                                <p className="text-xs opacity-90">Super Admin</p>
                            </div>
                            <button 
                                onClick={handleLogout}
                                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                                title="Logout"
                            >
                                <PowerOff className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Page Title */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Upload className="w-8 h-8 text-[#E91E63]" />
                        <h2 className="text-3xl font-bold text-gray-900">Upload New SOP</h2>
                    </div>
                    <p className="text-gray-600">Upload a new Standard Operating Procedure with electronic signature</p>
                </div>

                {/* Instructions Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-6 h-6 text-[#E91E63]" />
                        <h3 className="text-xl font-bold text-gray-900">Upload Instructions</h3>
                    </div>
                    <ol className="space-y-2 mb-6 text-gray-700">
                        <li className="flex gap-3">
                            <span className="font-bold text-[#E91E63] min-w-[24px]">1.</span>
                            <span>Fill in all required fields (marked with <span className="text-red-600 font-bold">*</span>)</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-[#E91E63] min-w-[24px]">2.</span>
                            <span>Select your SOP document file (PDF or HTML, max 50MB)</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-[#E91E63] min-w-[24px]">3.</span>
                            <span>Review all information carefully before uploading</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-[#E91E63] min-w-[24px]">4.</span>
                            <span>Click "Upload SOP" and provide your electronic signature</span>
                        </li>
                        <li className="flex gap-3">
                            <span className="font-bold text-[#E91E63] min-w-[24px]">5.</span>
                            <span>Your PIN/password will be required for 21 CFR Part 11 compliance</span>
                        </li>
                    </ol>
                    <div className="bg-gradient-to-br from-[#475569]/5 to-[#E91E63]/10 border-l-4 border-[#E91E63] p-4 rounded-r-lg">
                        <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-[#E91E63] mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-bold text-gray-900 mb-1">21 CFR Part 11 Compliance</p>
                                <p className="text-sm text-gray-700">This action requires your electronic signature. You will be prompted to enter your PIN to certify authorship of this document.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Upload Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8">
                    <SOPUploadForm onSuccess={handleUploadSuccess} />
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">
                        KLINiSOL
                    </h3>
                    <p className="text-sm opacity-90">&copy; 2025 KLINISOL. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default SOPUpload;